<template>
	<div class="flightView">
		<main role="main" id="yourflight">
			<div class="path-frontpage">
				<div class="container">
					<div class="main-content">
						<img class="main-img" src="@/assets/images/yourflight_main.jpg" alt="" />
						<div class="main-title">
							<div class="max-width">
								<h1 class="h2">Your Flight #{{flight.reference}} | {{flight.type}}</h1>
								<p>Select Legs to <strong>Fill/Modify</strong> and <strong>Update your flight</strong></p>
							</div>
						</div>
						<section class="flights">
							<div class="max-width">
								<div class="flight-contener">
									<div class="flight-content" id="accordion">
											<div
												v-for="(leg, legIndex) in flight.legs"
												:key="legIndex"
												class="file-collapsable"
											>
											<div class="card-header-block" id="headingOne">
												<b-button
													class="intit-up collapsed"
													v-b-toggle="`leg-accordion-${legIndex}`"
													block
												>
													LEG {{leg.order}} | {{renderLocationName(leg.from_location)}} - {{renderLocationName(leg.to_location)}} | {{$dayjs(leg.date).format('DD/MM')}}
												</b-button>
											</div>
											<b-collapse :id="`leg-accordion-${legIndex}`" accordion="leg-accordion" role="tabpanel">
												<section v-if="leg.type === 'GROUND'" class="ground-leg radio-button">
													<h2>{{leg.from_location}} - {{leg.to_location}}</h2>
													<h3>Journey</h3>
													<p>{{$dayjs(leg.date).format('DD/MM/YYYY')}}<span v-if="leg.departure_time"> | Departure {{leg.departure_time}}</span><span v-if="leg.arrival_time"> | Arrival {{leg.arrival_time}}</span></p>
													<p v-if="leg.limo_service_type"><strong>Service:</strong> {{renderGroundValue(leg.limo_service_type)}}<span v-if="leg.limo_disposal_hours"> ({{leg.limo_disposal_hours}} h)</span></p>
													<h4>Pick-up</h4>
													<p>{{leg.limo_pickup_address || leg.from_location}}</p>
													<p v-if="leg.limo_pickup_map_link"><a :href="leg.limo_pickup_map_link" target="_blank" rel="noopener noreferrer">Map</a></p>
													<template v-if="leg.limo_stops && leg.limo_stops.length">
														<h4>Stops</h4>
														<p>{{leg.limo_stops.join(' → ')}}</p>
													</template>
													<h4>Drop-off</h4>
													<p>{{leg.limo_dropoff_address || leg.to_location}}</p>
													<p v-if="leg.limo_dropoff_map_link"><a :href="leg.limo_dropoff_map_link" target="_blank" rel="noopener noreferrer">Map</a></p>
													<template v-if="leg.limo_is_meet_greet">
														<h4>Meet &amp; greet</h4>
														<p>{{leg.limo_meet_greet_sign_name}}<span v-if="leg.limo_meet_greet_location"> ({{renderGroundValue(leg.limo_meet_greet_location)}})</span></p>
													</template>
													<template v-if="leg.limo_vehicle_category || leg.limo_vehicle_model || leg.limo_vehicle_plate">
														<h3>Vehicle</h3>
														<p>{{[renderGroundValue(leg.limo_vehicle_category), leg.limo_vehicle_model, leg.limo_vehicle_color].filter(Boolean).join(' · ')}}</p>
														<p v-if="leg.limo_vehicle_plate"><strong>Plate:</strong> {{leg.limo_vehicle_plate}}</p>
													</template>
													<template v-if="leg.limo_driver_name || leg.limo_driver_phone">
														<h3>Driver</h3>
														<p>{{leg.limo_driver_name}}<span v-if="leg.limo_driver_phone"> | <a :href="`tel:${leg.limo_driver_phone}`">{{leg.limo_driver_phone}}</a></span></p>
														<p v-if="leg.limo_driver_languages && leg.limo_driver_languages.length"><strong>Languages:</strong> {{leg.limo_driver_languages.join(', ')}}</p>
													</template>
													<h3>Passengers</h3>
													<p>{{leg.pax}} pax<span v-if="leg.luggages"> | {{leg.luggages}} luggage(s)</span></p>
													<p v-if="leg.pax_names" style="white-space: pre-line;">{{leg.pax_names}}</p>
												</section>
												<template v-else>
												<section class="radio-button">
													<h2>{{leg.from_location}} - {{leg.to_location}}</h2>
													<h3>Departure Time</h3>
													<div class="radio-huge">
														<div
															v-for="schedule in departureTimeRange[leg.id]"
															:key="`${leg.id}-schedule-${schedule}`"
															class="ui-radio"
														>
															<input
																v-model="leg.departure_time"
																type="radio"
																:id="`schedule-radio-${leg.id}-${schedule}`"
																:name="`schedule-radio-${leg.id}`"
																:value="schedule"
															>
															<label :for="`schedule-radio-${leg.id}-${schedule}`">{{schedule}}</label>
															<div class="check"></div>
														</div>
													</div>
												</section>
												<section class="img">
													<img class="simple-img" src="@/assets/images/yourflight_guests.png" alt="" />
												</section>
												<section class="guests">
													<h3>Guests</h3>
													<div class="touchspins">
														<addSubtractItem
															label="Men (16+ yo)"
															:value="leg.men_number"
															@subtract="subtract(leg, 1, 'men_number')"
															@add="add(leg, 1, 'men_number')"
														/>
														<addSubtractItem
															label="Women (16+ yo)"
															:value="leg.women_number"
															@subtract="subtract(leg, 1, 'women_number')"
															@add="add(leg, 1, 'women_number')"
														/>
														<addSubtractItem
															label="Children (2-15 yo)"
															:value="leg.children_number"
															@subtract="subtract(leg, 1, 'children_number')"
															@add="add(leg, 1, 'children_number')"
														/>
														<addSubtractItem
															label="Baby (under 2 yo)"
															:value="leg.baby_number"
															@subtract="subtract(leg, 1, 'baby_number')"
															@add="add(leg, 1, 'baby_number')"
														/>
														<addSubtractItem
															label="Dogs"
															:value="leg.dogs_number"
															@subtract="subtract(leg, 1, 'dogs_number')"
															@add="add(leg, 1, 'dogs_number')"
														/>
														<addSubtractItem
															label="Cats"
															:value="leg.cats_number"
															@subtract="subtract(leg, 1, 'cats_number')"
															@add="add(leg, 1, 'cats_number')"
														/>
														<addSubtractItem
															label="Others"
															:value="leg.other_guests_number"
															@subtract="subtract(leg, 1, 'other_guests_number')"
															@add="add(leg, 1, 'other_guests_number')"
														/>

														<div
															v-if="leg.other_guests_number > 0"
															class="textarea">
															<textarea
																v-model="leg.other_guests_precision"
																placeholder="Please Specify"
															>
															</textarea>
														</div>
													</div>
													<div
														v-if='leg.dogs_number > 0'
														class="valid-checks"
													>
														<div class="valid-check">
															<p>If Dogs: </p>
																<div class="check-box">
																	<div class="form">
																		<input v-model="leg.dogs_guest_is_small" type="checkbox" :id="`${legIndex}-dogs_guest_is_small`" :name="`${legIndex}-dogs_guest_is_small`" class="" :value="true">
																		<label :for="`${legIndex}-dogs_guest_is_small`">Small</label>
																	</div>
																</div>
																<div class="check-box">
																	<div class="form">
																		<input v-model="leg.dogs_guest_is_medium" type="checkbox" :id="`${legIndex}-dogs_guest_is_medium`" :name="`${legIndex}-dogs_guest_is_medium`" class="" :value="true">
																		<label :for="`${legIndex}-dogs_guest_is_medium`">Medium</label>
																	</div>
																</div>
																<div class="check-box">
																	<div class="form">
																		<input v-model="leg.dogs_guest_is_big" type="checkbox" :id="`${legIndex}-dogs_guest_is_big`" :name="`${legIndex}-dogs_guest_is_big`" class="" :value="true">
																		<label :for="`${legIndex}-dogs_guest_is_big`">Big</label>
																	</div>
																</div>
														</div>
														<div class="valid-check valid-check-travel">
															<p>Travelling in</p>
																<div class="check-box">
																	<div class="form">
																		<input v-model="leg.dogs_guest_is_travelling_in_bag" type="checkbox" :id="`${legIndex}-dogs_guest_is_travelling_in_bag`"  :name="`${legIndex}-dogs_guest_is_travelling_in_bag`" class="" :value="true">
																		<label :for="`${legIndex}-dogs_guest_is_travelling_in_bag`">Bag</label>
																	</div>
																</div>
																<div class="check-box">
																	<div class="form">
																		<input v-model="leg.dogs_guest_is_travelling_free" type="checkbox" :id="`${legIndex}-dogs_guest_is_travelling_free`" :name="`${legIndex}-dogs_guest_is_travelling_free`" class="" :value="true">
																		<label :for="`${legIndex}-dogs_guest_is_travelling_free`">Free (on leash only)</label>
																	</div>
																</div>
														</div>
													</div>
												</section>

												<section class="img">
													<img class="simple-img" src="@/assets/images/yourflight_luggage.png" alt="" />
												</section>


												<section class="luggage">
													<h3>Luggage</h3>
													<div class="touchspins">
														<addSubtractItem
															label="Carry-On"
															:value="leg.luggages_carry_on_number"
															@subtract="subtract(leg, 1, 'luggages_carry_on_number')"
															@add="add(leg, 1, 'luggages_carry_on_number')"
														/>
														<addSubtractItem
															label="Hold Luggage"
															:value="leg.luggages_hold_number"
															@subtract="subtract(leg, 1, 'luggages_hold_number')"
															@add="add(leg, 1, 'luggages_hold_number')"
														/>
														<addSubtractItem
															label="Skis"
															:value="leg.luggages_skis_number"
															@subtract="subtract(leg, 1, 'luggages_skis_number')"
															@add="add(leg, 1, 'luggages_skis_number')"
														/>
														<addSubtractItem
															label="Golf Bag"
															:value="leg.luggages_golf_bag_number"
															@subtract="subtract(leg, 1, 'luggages_golf_bag_number')"
															@add="add(leg, 1, 'luggages_golf_bag_number')"
														/>
														<addSubtractItem
															label="Others (oversized or heavy)"
															:value="leg.luggages_others_oversized_number"
															@subtract="subtract(leg, 1, 'luggages_others_oversized_number')"
															@add="add(leg, 1, 'luggages_others_oversized_number')"
														/>
														<div
															v-if="leg.luggages_others_oversized_number > 0"
															class="textarea">
															<textarea
																v-model="leg.luggages_others_precision"
																placeholder="Please Specify"
															>
															</textarea>
														</div>
													</div>

												</section>

												<section class="img">
													<img class="simple-img" src="@/assets/images/yourflight_catering.png" alt="" />
												</section>

												<section class="catering">
													<h3>Catering</h3>
													<div class="radio-button">
														<div class="radio-simple radio-block">
															<div class="ui-radio">
																<input v-model="leg.catering_type" value="Standard" type="radio" :id="`${legIndex}-catering_type-Standard`" :name="`${legIndex}-radio-selector-catering_type`">
																<label :for="`${legIndex}-catering_type-Standard`">Standard (salty & sweet mix)</label>
																<div class="check"></div>
															</div>
															<div class="ui-radio">
																<input v-model="leg.catering_type" value="Sandwiches" type="radio" :id="`${legIndex}-catering_type-Sandwiches`" :name="`${legIndex}-radio-selector-catering_type`">
																<label :for="`${legIndex}-catering_type-Sandwiches`">Sandwiches & Salads + Standard (€)</label>
																<div class="check"></div>
															</div>
															<div class="ui-radio">
																<input v-model="leg.catering_type" value="Gastronomic" type="radio" :id="`${legIndex}-catering_type-Gastronomic`" :name="`${legIndex}-radio-selector-catering_type`">
																<label :for="`${legIndex}-catering_type-Gastronomic`">Gastronomic + Standard (€€)</label>
																<div class="check"></div>
															</div>
														</div>
													</div>
													<div class="valid-checks">
														<div class="valid-check valid-check-special">
															<p>Special request (specify below)</p>
															<div class="check-box">
																<div class="form">
																	<input v-model="leg.catering_request_is_casher" type="checkbox" :id="`${legIndex}-catering_request_is_casher`" class="" :value="true">
																	<label :for="`${legIndex}-catering_request_is_casher`">Casher</label>
																</div>
															</div>
															<div class="check-box">
																<div class="form">
																	<input v-model="leg.catering_request_is_hallal" type="checkbox" :id="`${legIndex}-catering_request_is_hallal`"  class="" :value="true">
																	<label :for="`${legIndex}-catering_request_is_hallal`">Halal</label>
																</div>
															</div>
															<div class="check-box">
																<div class="form">
																	<input v-model="leg.catering_request_is_vegan" type="checkbox" :id="`${legIndex}-catering_request_is_vegan`" class="" :value="true">
																	<label :for="`${legIndex}-catering_request_is_vegan`">Vegan</label>
																</div>
															</div>
														</div>
														<div class="textarea">
															<textarea
																v-model="leg.catering"
																placeholder=""
															>
															</textarea>
														</div>
													</div>
													<div class="valid-checks">
														<div class="valid-check">
															<p>Allergies</p>
															<div class="check-box">
																<div class="form">
																	<input v-model="leg.catering_is_allergy" type="radio" :id="`${legIndex}-catering_is_allergy-1`" :name="`${legIndex}-catering_is_allergy`" class="" :value="true">
																	<label class="ml-2" :for="`${legIndex}-catering_is_allergy-1`">Yes</label>
																</div>
															</div>
															<div class="check-box">
																<div class="form">
																	<input v-model="leg.catering_is_allergy" type="radio" :id="`${legIndex}-catering_is_allergy-2`"  :name="`${legIndex}-catering_is_allergy`" class="" :value="false">
																	<label class="ml-2" :for="`${legIndex}-catering_is_allergy-2`">No</label>
																</div>
															</div>
														</div>
														<div v-if="leg.catering_is_allergy" class="textarea">
															<p>If yes please specify</p>
															<textarea v-model="leg.catering_allergies" placeholder=""></textarea>
														</div>
													</div>
												</section>

												<section
													v-if="isFLightTimeMoreThanFiveHours(leg.flight_time)"
													class="img"
												>
													<img class="simple-img" src="@/assets/images/yourflight_service.jpg" alt="" />
												</section>

												<section
													v-if="isFLightTimeMoreThanFiveHours(leg.flight_time)"
													class="services"
												>
													<h3>On-board Services</h3>
													<div class="radio-button">
														<div class="radio-simple radio-block">
															<div class="ui-radio">
																<input v-model="leg.onboard_service_type" type="radio" :id="`${legIndex}-onboard_service_type-Standard`" :name="`${legIndex}-onboard_service_type-radio`" value="Standard">
																<label :for="`${legIndex}-onboard_service_type-Standard`">Standard Plenitude Package</label>
																<div class="check"></div>
															</div>
															<div class="ui-radio">
																<input v-model="leg.onboard_service_type" type="radio" :id="`${legIndex}-onboard_service_type-Premium`" :name="`${legIndex}-onboard_service_type-radio`"  value="Premium">
																<label :for="`${legIndex}-onboard_service_type-Premium`">Premium Plenitude Package</label>
																<div class="check"></div>
															</div>
														</div>
													</div>

													<div class="valid-checks">
														<div class="valid-check valid-check-special">
															<p>Special request</p>
															<div class="check-box">
																<div class="form">
																	<input v-model="leg.onboard_service_is_cosmetology" type="checkbox" :id="`${legIndex}-onboard_service_is_cosmetology`" class="" :value="true">
																	<label :for="`${legIndex}-onboard_service_is_cosmetology`">Cosmetology</label>
																</div>
															</div>
															<div class="check-box">
																<div class="form">
																	<input v-model="leg.onboard_service_is_massage" type="checkbox" :id="`${legIndex}-onboard_service_is_massage`" class="" :value="true">
																	<label :for="`${legIndex}-onboard_service_is_massage`">Massage</label>
																</div>
															</div>
														</div>
														<div class="textarea">
															<textarea v-model="leg.onboard_service_special_request" placeholder=""></textarea>
														</div>
													</div>
												</section>

												<section class="img">
													<img class="simple-img" src="@/assets/images/yourflight_transportation.png" alt="" />
												</section>

												<section class="transportation">
													<h3>Ground Transportation</h3>
													<div class="radio-button">
														<h4>Departure {{leg.from_location}}</h4>
														<div class="radio-simple radio-block">
															<div class="ui-radio">
																<input v-model="leg.from_location_taxi_type" type="radio" :id="`${legIndex}-from_location_taxi_type-Not-required`" :name="`${legIndex}-radio-selector-from_location_taxi_type`" value="Not required">
																<label :for="`${legIndex}-from_location_taxi_type-Not-required`">Not required</label>
																<div class="check"></div>
															</div>
															<div class="ui-radio">
																<input v-model="leg.from_location_taxi_type" type="radio" :id="`${legIndex}-from_location_taxi_type-Taxi`" :name="`${legIndex}-radio-selector-from_location_taxi_type`" value="Taxi">
																<label :for="`${legIndex}-from_location_taxi_type-Taxi`">Normal Taxi</label>
																<div class="check"></div>
															</div>
															<div class="ui-radio">
																<input v-model="leg.from_location_taxi_type" type="radio" :id="`${legIndex}-from_location_taxi_type-Limousine`" :name="`${legIndex}-radio-selector-from_location_taxi_type`" value="Limousine">
																<label :for="`${legIndex}-from_location_taxi_type-Limousine`">Limousine (Mercedes Class S or equivalent) (€)</label>
																<div class="check"></div>
															</div>
															<div class="ui-radio">
																<input v-model="leg.from_location_taxi_type" type="radio" :id="`${legIndex}-from_location_taxi_type-Van`" :name="`${legIndex}-radio-selector-from_location_taxi_type`" value="Van">
																<label :for="`${legIndex}-from_location_taxi_type-Van`">Van (Mercedes Vito or equivalent) (€€)</label>
																<div class="check"></div>
															</div>
															<div class="textarea">
																<p>Please specify pick up place</p>
																<textarea v-model="leg.from_location_pickup_place" placeholder=""></textarea>
															</div>
														</div>
													</div>

													<div class="radio-button">
														<h4>Arrival {{leg.to_location}}</h4>
														<div class="radio-simple radio-block">
															<div class="ui-radio">
																<input v-model="leg.to_location_taxi_type" type="radio" :id="`${legIndex}-to_location_taxi_type-Not-required`" :name="`${legIndex}-radio-selector-to_location_taxi_type`" value="Not required">
																<label :for="`${legIndex}-to_location_taxi_type-Not-required`">Not required</label>
																<div class="check"></div>
															</div>
															<div class="ui-radio">
																<input v-model="leg.to_location_taxi_type" type="radio" :id="`${legIndex}-to_location_taxi_type-Taxi`" :name="`${legIndex}-radio-selector-to_location_taxi_type`" value="Taxi">
																<label :for="`${legIndex}-to_location_taxi_type-Taxi`">Normal Taxi</label>
																<div class="check"></div>
															</div>
															<div class="ui-radio">
																<input v-model="leg.to_location_taxi_type" type="radio" :id="`${legIndex}-to_location_taxi_type-Limousine`" :name="`${legIndex}-radio-selector-to_location_taxi_type`" value="Limousine">
																<label :for="`${legIndex}-to_location_taxi_type-Limousine`">Limousine (Mercedes Class S or equivalent) (€)</label>
																<div class="check"></div>
															</div>
															<div class="ui-radio">
																<input v-model="leg.to_location_taxi_type" type="radio" :id="`${legIndex}-to_location_taxi_type-Van`" :name="`${legIndex}-radio-selector-to_location_taxi_type`" value="Van">
																<label :for="`${legIndex}-to_location_taxi_type-Van`">Van (Mercedes Vito or equivalent) (€€)</label>
																<div class="check"></div>
															</div>
															<div class="textarea">
																<p>If Limo or Van please specify Destination</p>
																<textarea v-model="leg.to_location_destination_place" placeholder=""></textarea>
															</div>
														</div>
													</div>
												</section>

												<section>
													<button class="btn btn-blue btn-large btn-rounded btn-send" @click="saveLeg(leg, legIndex)">Update your flight</button>
												</section>
												</template>
											</b-collapse>
										</div>
									</div>
								</div>
							</div>
						</section>
					</div>
				</div>
			</div>
		</main>
	</div>
</template>

<script>
import { APIService } from '@/api/APIService'
import addSubtractItem from '@/components/addSubtractItem'


const apiService = new APIService()

export default {
	name: 'FlightView',
	components: {
		addSubtractItem
	},
	data: function () {
		return {
			departureTimeRange: {},
			flight: {
				legs: [],
				reference: null,
				type: null
			},
		}
	},
	created: function() {
		this.$dayjs.locale('fr')
		this.getFlight()
	},
	methods: {
		renderGroundValue (value) {
			return value ? value.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase()) : ''
		},
		add (leg, nb, type) {
			leg[type] = leg[type] + nb
		},
		generateDepartureTimeRange(legs) {
			this.departureTimeRange = legs.reduce((range, leg) => {
				const departureTime = this.$dayjs(`2020-01-01 ${leg.departure_time}`, 'YYYY-MM-DD HH:mm')
				range[leg.id] = [
					departureTime.subtract(30, 'minute').format('HH:mm'),
					departureTime.subtract(15, 'minute').format('HH:mm'),
					leg.departure_time,
					departureTime.add(15, 'minute').format('HH:mm'),
					departureTime.add(30, 'minute').format('HH:mm'),
				]
				return range
			}, {})
		},
		getFlight () {
			this.$store.commit('loadFromAPI')
			apiService.getFlight(this.$route.params.daymonth, this.$route.params.reference)
			.then((result) => {
				this.flight = result.data
				this.generateDepartureTimeRange(this.flight.legs)
			})
			.catch((error) => {
				throw new Error('Flight Details getFlight API Error : ' + String(error))
			})
			.finally(() => {
				this.$store.commit('endLoadingFromAPI')
			})
		},
		isFLightTimeMoreThanFiveHours (flightTime) {
			if (flightTime) {
				try {
					if (parseFloat(flightTime.split(":")[0]) < 5) {
						return false
					}
				}
				catch (error) {
					console.log(error)
				}

			}
			return true
		},
		renderLocationName (location) {
			return location.split(' ')[0]
		},
		saveLeg (leg, legIndex) {
			this.$store.commit('loadFromAPI')
			apiService.saveLeg(this.$route.params.reference, leg)
			.then(() => {
				this.$root.$bvToast.toast(`Your information have been sent`, {
          title: 'Information saved',
          autoHideDelay: 5000,
          appendToast: false,
          variant: "success"
        })
				this.$root.$emit('bv::toggle::collapse', `leg-accordion-${legIndex}`)
			})
			.catch((error) => {
				throw new Error('Flight Details getFlight API Error : ' + String(error))
			})
			.finally(() => {
				this.$store.commit('endLoadingFromAPI')
			})
		},
		subtract (leg, nb, type) {
			if (leg[type] > 0) {
				leg[type] = leg[type] - nb
			}
		}
	}
}
</script>

<style>

/* Specific css */
. comp
.compteur-item-value {
	display: flex;
}

#yourflight * {
  margin: 0;
  border: 0;
  padding: 0; }
#yourflight html {
  height: 100%;
  padding: 0; }
#yourflight body {
  overflow: scroll;
  overflow-x: hidden;
  line-height: 26px;
  height: 100%;
  padding: 0;
  max-height: 999999px;
  font-size: 15px;
  color: #2b2b2b;
  background-color: #ffffff;
  font-family: "proxima-nova",sans-serif;
  font-weight: 400; }
#yourflight .container {
  width: 100%;
  max-width: 100%;
  position: relative;
  overflow: hidden;
  padding: 0; }
  @media only screen and (min-width: 991px) and (max-width: 1299px) {
    #yourflight .container {
      width: inherit;
      max-width: inherit; } }
  @media only screen and (max-width: 991px) {
    #yourflight .container {
      width: 100%;
      max-width: 100%; } }
#yourflight .max-width {
  position: relative;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto; }
  @media only screen and (max-width: 1300px) {
    #yourflight .max-width {
      max-width: 100%; } }
#yourflight a {
  position: relative;
  text-decoration: none;
  display: block;
  cursor: pointer;
  -moz-transition: color 0.5s ease;
  -o-transition: color 0.5s ease;
  -webkit-transition: color 0.5s ease;
  transition: color 0.5s ease;
  color: #000000; }
  #yourflight a:hover, #yourflight a:focus {
    text-decoration: none;
    color: #000000; }
#yourflight ul,
#yourflight li {
  margin: 0;
  list-style-type: none; }
#yourflight img {
  border: 0;
  display: block; }
#yourflight label {
  margin: 0; }
#yourflight p,
#yourflight figure {
  margin: 0;
  padding: 0; }
#yourflight strong {
  font-weight: 700; }
#yourflight form {
  width: 100%;
  height: 100%; }
#yourflight input:not([type]) {
  border-bottom: none;
  height: inherit;
  font-size: inherit;
  margin: 0;
  box-sizing: inherit; }
  #yourflight input:not([type]):focus:not([readonly]) {
    border-bottom: none;
    box-shadow: none; }
#yourflight input::-ms-clear {
  width: 0;
  height: 0; }
#yourflight label:focus,
#yourflight input:focus {
  outline: none; }
#yourflight input[type=text],
#yourflight input[type=password],
#yourflight input[type=email],
#yourflight input[type=tel],
#yourflight input[type=number],
#yourflight input[type=search],
#yourflight textarea.materialize-textarea {
  height: inherit;
  font-size: inherit;
  margin: 0;
  box-sizing: inherit; }
  #yourflight input[type=text]:focus:not([readonly]),
  #yourflight input[type=password]:focus:not([readonly]),
  #yourflight input[type=email]:focus:not([readonly]),
  #yourflight input[type=tel]:focus:not([readonly]),
  #yourflight input[type=number]:focus:not([readonly]),
  #yourflight input[type=search]:focus:not([readonly]),
  #yourflight textarea.materialize-textarea:focus:not([readonly]) {
    box-shadow: none; }
#yourflight .main-title {
  padding-bottom: 48px; }
  #yourflight .main-title .max-width {
    text-align: center; }
    #yourflight .main-title .max-width .h2 {
      font-size: 48px;
      font-weight: 500;
      line-height: 57px;
      letter-spacing: 0.5px;
      padding-bottom: 20px; }
      @media only screen and (max-width: 767px) {
        #yourflight .main-title .max-width .h2 {
          font-size: 35px;
          line-height: 42px; } }
      @media only screen and (max-width: 650px) {
        #yourflight .main-title .max-width .h2 {
          font-size: 32px;
          line-height: 38px; } }
      @media only screen and (max-width: 480px) {
        #yourflight .main-title .max-width .h2 {
          font-size: 23px;
          line-height: 23px; } }
      @media only screen and (max-width: 320px) {
        #yourflight .main-title .max-width .h2 {
          font-size: 20px;
          line-height: 20px; } }
    #yourflight .main-title .max-width p {
      color: #6d6d6d; }
      #yourflight .main-title .max-width p strong {
        text-transform: uppercase;
      }
      @media only screen and (max-width: 650px) {
        #yourflight .main-title .max-width p {
          font-size: 12px;
 } }
#yourflight .container .main-img {
  object-fit: cover;
  width: 100%;
  height: 300px;
  margin-bottom: 120px; }
  @media only screen and (max-width: 767px) {
    #yourflight .container .main-img {
      margin-bottom: 45px; } }
#yourflight .container .simple-img {
  object-fit: cover;
	margin-left: auto;
	margin-right: auto;
  width: 80%;
	/*height: 200px;*/ }
  @media only screen and (max-width: 767px) {
    #yourflight .container .simple-img {
      height: 275px; } }
  @media only screen and (max-width: 380px) {
    #yourflight .container .simple-img {
      height: 200px; } }
#yourflight .container .main-content section {
  margin-bottom: 60px;
  padding: 0; }
  @media only screen and (max-width: 767px) {
    #yourflight .container .main-content section {
      margin-bottom: 40px; } }
#yourflight .container .main-content h1,
#yourflight .container .main-content h2,
#yourflight .container .main-content h3,
#yourflight .container .main-content h4,
#yourflight .container .main-content h5 {
  display: block;
  margin: 0; }
#yourflight .container .main-content h2 {
  text-align: center;
  font-size: 36px;
  line-height: 30px;
  font-weight: 700;
  margin-bottom: 40px;
  margin-top: 25px; }
  @media only screen and (max-width: 767px) {
    #yourflight .container .main-content h2 {
      font-size: 20px;
      margin-bottom: 0; } }
#yourflight .container .main-content h3 {
  text-align: center;
  margin-bottom: 15px; }
  @media only screen and (max-width: 767px) {
    #yourflight .container .main-content h3 {
      font-size: 20px; } }
#yourflight .container .main-content h4 {
  text-align: center;
  color: #437abd; }
#yourflight .container .main-content textarea {
  display: block;
  width: 100%;
  border-width: 1px;
  border-style: solid;
  border-color: #f4f4f4;
  -webkit-border-radius: 4px;
  -moz-border-radius: 4px;
  border-radius: 4px;
  min-height: 85px;
  padding: 10px 15px;
  background-color: #f4f4f4; }
  /*
#yourflight .container .main-content .textarea {
  margin-top: 20px; }
  */
  #yourflight .container .main-content .textarea p {
    margin-bottom: 5px;
    text-align: left; }
#yourflight .container .main-content .guests .valid-check {
  margin-top: 20px; }
#yourflight .container .main-content .touchspins {
  margin-bottom: 60px; }
  @media only screen and (max-width: 650px) {
    #yourflight .container .main-content .touchspins {
      padding-left: 15px;
      padding-right: 15px; } }
  #yourflight .container .main-content .touchspins > div {
		align-items: center;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    width: 600px;
    margin: 0 auto 25px auto; }
  #yourflight .container .main-content .touchspins > div:last-child {
    margin-bottom: -30px; }
    @media only screen and (max-width: 650px) {
      #yourflight .container .main-content .touchspins > div {
        width: 100%; } }
    #yourflight .container .main-content .touchspins > div.touch-others .textarea {
      width: 63.5%;
      display: none; }
    #yourflight .container .main-content .touchspins > div.touch-others.explanation .textarea {
      display: block;
      width: 100%; }
    #yourflight .container .main-content .touchspins > div .touchspin {
			align-items: center;
      display: flex;
      width: 149px;
      border-width: 1px;
      border-style: solid;
      border-color: none;
      border: none;
      padding-left: 15px;
			padding-right: 15px;
		}
		#yourflight .container .main-content .touchspins > div .touchspin > .value {
			font-size: 28px;
			color: black;
		}
		#yourflight .container .main-content .touchspins > div .touchspin > .compteur-item {
			font-size: 48px;
			padding-left: 0px;
			color:#437abd;
			cursor: pointer;
		}

    #yourflight .container .main-content .touchspins > div p {
      min-width: 130px;
      height: 40px;
      line-height: 40px;
      text-align: left; }
    #yourflight .container .main-content .touchspins > div .form-control {
      height: 40px !important;
      padding-left: 0 !important;
      padding-right: 0 !important;
      font-size: 24px !important; }
      @media only screen and (max-width: 650px) {
        #yourflight .container .main-content .touchspins > div .form-control {
          font-size: 24px !important; } }
    #yourflight .container .main-content .touchspins > div .bootstrap-touchspin {
      display: flex;
      flex-wrap: inherit; }
      #yourflight .container .main-content .touchspins > div .bootstrap-touchspin button,
      #yourflight .container .main-content .touchspins > div .bootstrap-touchspin .input-group-btn,
      #yourflight .container .main-content .touchspins > div .bootstrap-touchspin .btn {
        width: 3rem;
        padding: 0;
        order: 2;
        font-size: 1.6rem; }
      #yourflight .container .main-content .touchspins > div .bootstrap-touchspin .input-group-addon {
        order: 1;}
    #yourflight .container .main-content .touchspins > div .input-group > .form-control {
      text-align: center;
      font-size: 1.5rem;
      font-weight: 500; }
    #yourflight .container .main-content .touchspins > div span.input-group-btn {
      margin: 0; }
      #yourflight .container .main-content .touchspins > div span.input-group-btn .btn {
        color: #437abd;
        font-size: 55px !important;
        font-weight: 400 !important;
        margin-top: 7px;}
      #yourflight .container .main-content .touchspins > div span.input-group-btn .btn.bootstrap-touchspin-down {
        color: #437abd;
        margin-top: 5px;
        font-size: 65px !important;
        font-weight: 400 !important;}


#yourflight .container .main-content .flight-contener .radio-button {
  padding-left: 30px;
  padding-right: 30px; }
  @media only screen and (max-width: 650px) {
    #yourflight .container .main-content .flight-contener .radio-button {
      padding-left: 15px;
      padding-right: 15px; } }
  #yourflight .container .main-content .flight-contener .radio-button .ui-radio {
    position: relative;
    width: 100%;
    height: 100%;
    cursor: pointer; }
    #yourflight .container .main-content .flight-contener .radio-button .ui-radio input[type=radio] {
      position: absolute;
      visibility: hidden; }
    #yourflight .container .main-content .flight-contener .radio-button .ui-radio label {
      font-weight: 300;
      font-size: 18px;
      padding-top: 7px;
      cursor: pointer; }
      @media only screen and (max-width: 480px) {
        #yourflight .container .main-content .flight-contener .radio-button .ui-radio label {
          font-size: 14px; } }
    #yourflight .container .main-content .flight-contener .radio-button .ui-radio [type="radio"]:checked + label:before,
    #yourflight .container .main-content .flight-contener .radio-button .ui-radio [type="radio"]:not(:checked) + label:before,
    #yourflight .container .main-content .flight-contener .radio-button .ui-radio [type="radio"]:checked + label:after,
    #yourflight .container .main-content .flight-contener .radio-button .ui-radio [type="radio"]:not(:checked) + label:after {
      display: none; }
  #yourflight .container .main-content .flight-contener .radio-button .radio-simple {
    width: 600px;
    margin: 30px auto;
    display: flex;
    justify-content: flex-start; }
    @media only screen and (max-width: 650px) {
      #yourflight .container .main-content .flight-contener .radio-button .radio-simple {
        width: 100%;
        flex-wrap: wrap; } }
    #yourflight .container .main-content .flight-contener .radio-button .radio-simple.radio-block {
      display: block; }
      #yourflight .container .main-content .flight-contener .radio-button .radio-simple.radio-block .ui-radio {
        margin: 0 0 10px 0; }
    #yourflight .container .main-content .flight-contener .radio-button .radio-simple p {
      min-width: 105px;
      text-align: left;
      padding-top: 2.5px; }
      @media only screen and (max-width: 650px) {
        #yourflight .container .main-content .flight-contener .radio-button .radio-simple p {
          min-width: 100%;
          width: 100%; } }
    #yourflight .container .main-content .flight-contener .radio-button .radio-simple .ui-radio {
      width: auto;
      display: block;
      margin-left: 30px;
      margin-right: 30px; }
      @media only screen and (max-width: 650px) {
        #yourflight .container .main-content .flight-contener .radio-button .radio-simple .ui-radio {
          margin-left: 0;
          margin-right: 12px; } }
      #yourflight .container .main-content .flight-contener .radio-button .radio-simple .ui-radio .check {
        display: block;
        width: 16px;
        height: 16px;
        position: absolute;
        top: 8px;
        left: 0;
        border-width: 1px;
        border-style: solid;
        border-color: #437abd;
        -webkit-border-radius: 50%;
        -moz-border-radius: 50%;
        border-radius: 50%;
        z-index: 5;
        -moz-transition: border 0.25s linear;
        -o-transition: border 0.25s linear;
        -webkit-transition: border 0.25s linear;
        transition: border 0.25s linear; }
        #yourflight .container .main-content .flight-contener .radio-button .radio-simple .ui-radio .check:hover {
          width: 16px;
          height: 16px;
          border-width: 1px;
          border-style: solid;
          border-color: #437abd; }
        #yourflight .container .main-content .flight-contener .radio-button .radio-simple .ui-radio .check:before {
          display: block;
          width: 50%;
          height: 50%;
          position: absolute;
          top: 5px;
          left: 5px;
          position: absolute;
          content: '';
          -webkit-border-radius: 2px;
          -moz-border-radius: 2px;
          border-radius: 2px;
          margin: auto;
          -moz-transition: background 0.25s linear;
          -o-transition: background 0.25s linear;
          -webkit-transition: background 0.25s linear;
          transition: background 0.25s linear; }
      #yourflight .container .main-content .flight-contener .radio-button .radio-simple .ui-radio input[type=radio]:checked ~ .check {
        border: 1px solid #437abd;
        background-color: #437abd; }
        #yourflight .container .main-content .flight-contener .radio-button .radio-simple .ui-radio input[type=radio]:checked ~ .check:before {
          width: 10px;
          height: 10px;
          position: absolute;
          top: 2px;
          left: 2px;
          background-color: #437abd;
          border: 2px solid #ffffff;
          -webkit-border-radius: 50%;
          -moz-border-radius: 50%;
          border-radius: 50%;
          content: '';
          text-align: center;
          font-size: 0.8em; }
    #yourflight .container .main-content .flight-contener .radio-button .radio-simple label {
      padding-left: 25px;
      text-align: left;
      width: 100%; }
  #yourflight .container .main-content .flight-contener .radio-button .radio-huge {
    display: flex;
    justify-content: center; }
    #yourflight .container .main-content .flight-contener .radio-button .radio-huge label {
      padding: 10px 15px;
      -webkit-border-radius: 12px;
      -moz-border-radius: 12px;
      border-radius: 12px; }
      @media only screen and (max-width: 320px) {
        #yourflight .container .main-content .flight-contener .radio-button .radio-huge label {
          padding: 10px; } }
    #yourflight .container .main-content .flight-contener .radio-button .radio-huge .ui-radio {
      display: flex;
      justify-content: center; }
      #yourflight .container .main-content .flight-contener .radio-button .radio-huge .ui-radio .check {
        z-index: -3000em;
        display: block;
        width: 0;
        height: 0; }
      #yourflight .container .main-content .flight-contener .radio-button .radio-huge .ui-radio input[type=radio]:checked ~ label {
        background-color: #437abd;
        font-weight: 700;
        color: #ffffff; }
#yourflight .container .main-content .flight-contener .valid-checks {
  width: 600px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 30px; }
  @media only screen and (max-width: 650px) {
    #yourflight .container .main-content .flight-contener .valid-checks {
      width: 100%;
      padding-left: 15px;
      padding-right: 15px; } }
  #yourflight .container .main-content .flight-contener .valid-checks .valid-check {
    display: flex;
    align-items: center;
    margin-bottom: 7.5px;
    flex-wrap: wrap;
    position: relative; }
    #yourflight .container .main-content .flight-contener .valid-checks .valid-check p {
      min-width: 105px;
      text-align: left; }
      @media only screen and (max-width: 650px) {
        #yourflight .container .main-content .flight-contener .valid-checks .valid-check p {
          min-width: 69px;} }
      @media only screen and (max-width: 650px) {
        #yourflight .container .main-content .flight-contener .valid-checks .valid-check.valid-check-special p {
          width: 100%; } }
        #yourflight .container .main-content .flight-contener .valid-checks .valid-check.valid-check-travel p {
          min-width: 110px; }
    #yourflight .container .main-content .flight-contener .valid-checks .valid-check .check-box {
      margin-left: 30px;
      margin-right: 30px; }
      @media only screen and (max-width: 650px) {
        #yourflight .container .main-content .flight-contener .valid-checks .valid-check .check-box {
          margin-left: 0;
          margin-right: 12px; } }
    #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form {
      margin: 0; }
      #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:not(:checked),
      #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:checked {
        position: absolute;
        left: -9999px; }
      #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:not(:checked) + label,
      #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:checked + label {
        width: 100%;
        position: relative;
        padding-left: 2.1rem;
        font-weight: 300;
        font-size: 18px;
        padding-top: 2px;
        cursor: pointer; }
        @media only screen and (max-width: 650px) {
          #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:not(:checked) + label,
          #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:checked + label {
            padding-left: 1.5rem; } }
        @media only screen and (max-width: 480px) {
          #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:not(:checked) + label,
          #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:checked + label {
            font-size: 14px; } }
      #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:not(:checked) + label:after,
      #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:checked + label:after,
      #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:not(:checked) + label:before,
      #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:checked + label:before {
        position: absolute;
        top: 0;
        left: 0;
        width: 1.6rem;
        height: 1.6rem;
        -webkit-border-radius: 5px;
        -moz-border-radius: 5px;
        border-radius: 5px; }
        @media only screen and (max-width: 650px) {
          #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:not(:checked) + label:after,
          #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:checked + label:after,
          #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:not(:checked) + label:before,
          #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:checked + label:before {
            width: 1.2rem;
            height: 1.2rem;
            top: 4px; } }
      #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:not(:checked) + label:before,
      #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:checked + label:before {
        border-width: 1px;
        border-style: solid;
        border-color: #000000; }
      #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:not(:checked) + label:after,
      #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:checked + label:after {
        text-align: center;
        padding-top: 0;
        content: '\2713';
        font-size: 1.5rem;
        line-height: 20px;
        color: #ffffff;
        background-color: #437abd;
        border: 1px solid #437abd;
        -moz-transition: all 0.2s ease;
        -o-transition: all 0.2s ease;
        -webkit-transition: all 0.2s ease;
        transition: all 0.2s ease; }
        @media only screen and (max-width: 650px) {
          #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:not(:checked) + label:after,
          #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:checked + label:after {
            font-size: 1rem;
            line-height: 15px; } }
      #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form [type="checkbox"]:not(:checked) + label:after {
        border: 1px solid #aaabac;
        background: #ffffff; }
      #yourflight .container .main-content .flight-contener .valid-checks .valid-check .form a {
        display: inline; }
#yourflight .container .main-content .flight-contener .file-collapsable {
  /*@include border(#437abd);*/
  margin-bottom: 20px;
  -webkit-border-radius: 12px;
  -moz-border-radius: 12px;
  border-radius: 12px; }
  #yourflight .container .main-content .flight-contener .file-collapsable .card-header-block .intit-up {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 50px;
    cursor: pointer;
    padding-left: 15px;
    padding-right: 15px;
    text-align: center;
    background-color: #437abd;
    color: #ffffff;
		box-shadow: none;
    /*
    .roundedd {
      position: relative;
      @include w-h-sim(60px);
      min-width: 60px;
      background-color: #437abd;
      @include radius(50%);
      margin-right: 30px;
      @media only screen and (max-width:767px) {
        @include w-h-sim(40px);
        min-width: 40px;
        margin-right: 20px;
      }
    }
    */ }
    @media only screen and (max-width: 767px) {
      #yourflight .container .main-content .flight-contener .file-collapsable .card-header-block .intit-up {
        padding-left: 10px;
        padding-right: 10px; } }
    #yourflight .container .main-content .flight-contener .file-collapsable .card-header-block .intit-up p {
      font-size: 20px;
      font-weight: 700;
      line-height: 30px;
      color: #ffffff; }
  #yourflight .container .main-content .flight-contener .file-collapsable .card-header-block .intit-up[aria-expanded="true"] {
    /*background-color: #437abd;
    color: $white;*/ }
    #yourflight .container .main-content .flight-contener .file-collapsable .card-header-block .intit-up[aria-expanded="true"] .roundedd:before {
      width: 16px;
      height: 2px;
      position: absolute;
      top: calc(50% - 1px);
      top: -moz-calc(50% - 1px);
      top: -webkit-calc(50% - 1px);
      top: calc(50% - 1px);
      top: -moz-calc(50% - 1px);
      top: -webkit-calc(50% - 1px);
      left: calc(50% - 8px);
      left: -moz-calc(50% - 8px);
      left: -webkit-calc(50% - 8px);
      left: calc(50% - 8px);
      left: -moz-calc(50% - 8px);
      left: -webkit-calc(50% - 8px);
      content: '';
      background: url(@/assets/images/minus.svg) center no-repeat; }
  #yourflight .container .main-content .flight-contener .file-collapsable .card-header-block .intit-up[aria-expanded="false"] .roundedd:before {
    width: 16px;
    height: 16px;
    position: absolute;
    top: calc(50% - 8px);
    top: -moz-calc(50% - 8px);
    top: -webkit-calc(50% - 8px);
    top: calc(50% - 8px);
    top: -moz-calc(50% - 8px);
    top: -webkit-calc(50% - 8px);
    left: calc(50% - 8px);
    left: -moz-calc(50% - 8px);
    left: -webkit-calc(50% - 8px);
    left: calc(50% - 8px);
    left: -moz-calc(50% - 8px);
    left: -webkit-calc(50% - 8px);
    content: '';
    background: url(@/assets/images/plus.svg) center no-repeat; }
  #yourflight .container .main-content .flight-contener .file-collapsable span {
    display: block;
    padding-left: 30px;
    padding-right: 20px;
    line-height: 24px;
    color: #808080;
    margin: 20px 0; }
	@media only screen and (max-width: 650px) {
		#yourflight .container .main-content .flight-contener .file-collapsable span {
			padding-left: 20px;
			padding-right: 20px; } }
#yourflight .container .main-content .flight-contener .passport {
  width: 600px;
  margin-left: auto;
  margin-right: auto; }
  @media only screen and (max-width: 650px) {
    #yourflight .container .main-content .flight-contener .passport {
      width: 100%; } }
  @media only screen and (max-width: 650px) {
    #yourflight .container .main-content .flight-contener .passport ul {
      padding-left: 15px;
      padding-right: 15px; } }
  #yourflight .container .main-content .flight-contener .passport ul li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px; }
  #yourflight .container .main-content .flight-contener .passport ul li p {
    text-align: left; }
  @media only screen and (max-width: 650px) {
    #yourflight .container .main-content .flight-contener .passport ul li span {
      width: 115px;
      text-align: center;
      padding-left: 5px;
      padding-right: 5px; } }
    #yourflight .container .main-content .flight-contener .passport ul li div {
      display: flex;
      align-items: flex-end; }
      #yourflight .container .main-content .flight-contener .passport ul li div button {
        margin-left: 10px; }
    @media only screen and (max-width: 650px) {
      #yourflight .container .main-content .flight-contener .passport ul li .btn.btn.btn-large {
        padding: 5px;
        font-size: 13px !important;
        min-width: 115px; } }
#yourflight .container .main-content .flight-contener .buttons {
  display: flex;
  justify-content: center; }
  #yourflight .container .main-content .flight-contener .buttons li {
    margin-left: 30px;
    margin-right: 30px;
    margin-top: 35px; }
#yourflight .container .main-content .flight-contener .btn {
  display: table;
  color: #ffffff;
  font-size: 16px !important;
  margin: auto;
  font-weight: 600; }
  #yourflight .container .main-content .flight-contener .btn.btn-blue {
    background: #437abd;
    border-color: #437abd;
    min-width: 170px; }
    @media only screen and (max-width: 480px) {
      #yourflight .container .main-content .flight-contener .btn.btn-blue {
        min-width: 130px; } }
  #yourflight .container .main-content .flight-contener .btn.btn-send {
    margin-top: 35px; }
  #yourflight .container .main-content .flight-contener .btn.btn-red {
    background: #FF6868;
    border-color: #FF6868;
    min-width: 170px; }
    @media only screen and (max-width: 480px) {
      #yourflight .container .main-content .flight-contener .btn.btn-red {
        min-width: 130px; } }
  #yourflight .container .main-content .flight-contener .btn.btn.btn-large {
    font-size: 14px;
    padding: 9px 34px;
    line-height: 25px; }
    @media only screen and (max-width: 650px) {
      #yourflight .container .main-content .flight-contener .btn.btn.btn-large {
        padding-left: 12px;
        padding-right: 12px; } }
  #yourflight .container .main-content .flight-contener .btn.btn-rounded {
    border-radius: 50px; }

/*# sourceMappingURL=flights.css.map */
</style>
